// Single formatter shared by every model-selection/attribution surface —
// composer dropdown (InputControls.tsx), the settings Models panel
// (settings/panels/ModelsPanel.tsx), and the per-message model stamp
// (ChatMessage.tsx) — so the three cannot independently drift on how a
// substrate/provider/authKind/free combination reads to a self-hoster.
import type { ModelEntry } from "./chat-config"

export type Substrate = ModelEntry["substrate"]
export type AuthKind = ModelEntry["authKind"]

const SUBSTRATE_DISPLAY: Record<Substrate, string> = {
  "claude-code": "Claude Code",
  codex: "Codex CLI",
  openrouter: "OpenRouter",
  hermes: "Hermes",
}

const PROVIDER_DISPLAY: Record<string, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  google: "Google",
  moonshot: "Moonshot",
  deepseek: "DeepSeek",
  "x-ai": "xAI",
  qwen: "Qwen",
  openrouter: "OpenRouter",
  minimax: "MiniMax",
}

const AUTH_DISPLAY: Record<AuthKind, string> = {
  subscription: "subscription",
  "api-key": "API key",
  login: "login",
}

export function substrateDisplayName(substrate: Substrate): string {
  return SUBSTRATE_DISPLAY[substrate] ?? substrate
}

export function providerDisplayName(provider: string): string {
  return PROVIDER_DISPLAY[provider] ?? provider
}

export function authKindDisplayName(authKind: AuthKind): string {
  return AUTH_DISPLAY[authKind] ?? authKind
}

/**
 * The quiet monospace subline shown under every model row on every
 * selection surface: substrate · provider · exact model id · auth kind —
 * except the last fact becomes "free" instead of the auth kind when the row
 * costs nothing (a free-tier OpenRouter model is more usefully described to
 * a self-hoster by "free" than by "API key").
 */
export function formatModelFacts(m: ModelEntry): string {
  const last = m.free ? "free" : authKindDisplayName(m.authKind)
  return `${substrateDisplayName(m.substrate)} · ${providerDisplayName(m.provider)} · ${m.resolvedModel} · ${last}`
}

/** Per-message footer stamp: "<label> · <substrate display name>". */
export function formatMessageStamp(m: Pick<ModelEntry, "label" | "substrate">): string {
  return `${m.label} · ${substrateDisplayName(m.substrate)}`
}

export interface ModelGroup {
  substrate: Substrate
  models: ModelEntry[]
}

/**
 * Groups models by substrate, preserving each group's first-appearance order
 * in `models` (which is registry order — CLAUDE CODE / CODEX CLI / HERMES,
 * matching MODEL_REGISTRY's own construction order in model-registry.ts).
 * Shared by the composer dropdown and the settings Models panel so they
 * cannot render the substrates in a different order from one another.
 */
export function groupBySubstrate(models: ModelEntry[]): ModelGroup[] {
  const groups: ModelGroup[] = []
  const bySubstrate = new Map<Substrate, ModelGroup>()
  for (const model of models) {
    let group = bySubstrate.get(model.substrate)
    if (!group) {
      group = { substrate: model.substrate, models: [] }
      bySubstrate.set(model.substrate, group)
      groups.push(group)
    }
    group.models.push(model)
  }
  return groups
}
